/** @jsx jsx */
import { customColors } from '@apollo/orbit';
import { css, jsx } from '@emotion/react';
import getIsChromatic from 'chromatic/isChromatic';
import * as monacoEditor from 'monaco-editor';
import { useEffect, useMemo, useRef, useState } from 'react';
import 'monaco-editor/esm/vs/editor/contrib/suggest/browser/suggestController';

import {
  MonacoLanguage,
  defineStudioMonacoThemes,
  useRegisterMonacoLanguageContribution,
} from 'src/app/graph/shared/monacoHelpers/monacoHelpers';
import { STUDIO_THEME_LIGHT } from 'src/components/editor/studioThemeLight';

import { DEFAULT_MONACO_OPTIONS } from './useStudioEditor';

const { text } = customColors;

defineStudioMonacoThemes();

const DIFF_EDITOR_OPTIONS: monacoEditor.editor.IStandaloneDiffEditorConstructionOptions =
  {
    enableSplitViewResizing: false,
    renderSideBySide: true,
    lineDecorationsWidth: 20,
    overviewRulerBorder: true,
    roundedSelection: true,
    wordWrap: 'off',
    readOnly: true,
    automaticLayout: true,
    scrollBeyondLastLine: false,
    theme: STUDIO_THEME_LIGHT,
    smoothScrolling: true,
    ...DEFAULT_MONACO_OPTIONS,
  };

// the editor onDidUpdateDiff listener fires significantly after the
// individual editor models onDidChangeContent listeners fire. Presumably monaco
// needs to do some heavy calculation to show the diffs.
// We want to avoid showing a weird state in between when the schema has been fetched &
// when the diff editor is updated. We continue to show the loading spinner until the diff is updated
export const useModelsAndDiffEditorOutOfSync = ({
  diffEditor,
}: {
  diffEditor: monacoEditor.editor.IDiffEditor;
}) => {
  const [modelsAndDiffOutOfSync, setModelsAndDiffOutOfSync] = useState(false);
  useEffect(() => {
    diffEditor.onDidUpdateDiff(() => {
      setModelsAndDiffOutOfSync(false);
    });
  }, [diffEditor]);
  useEffect(() => {
    const modifiedDisposable = diffEditor
      .getModel()
      ?.modified.onDidChangeContent(() => {
        setModelsAndDiffOutOfSync(true);
      });
    const originalDisposable = diffEditor
      .getModel()
      ?.original.onDidChangeContent(() => {
        setModelsAndDiffOutOfSync(true);
      });
    return () => {
      modifiedDisposable?.dispose();
      originalDisposable?.dispose();
    };
  }, [diffEditor]);
  return modelsAndDiffOutOfSync;
};

export const useStudioDiffEditor = ({
  language,
  originalFile,
  modifiedFile,
  monacoOptions,
}: {
  language: MonacoLanguage;
  originalFile: string | undefined;
  modifiedFile: string | undefined;
  monacoOptions?: Pick<
    monacoEditor.editor.IStandaloneEditorConstructionOptions,
    'contextmenu' | 'glyphMargin' | 'lineNumbers' | 'readOnly' | 'wordWrap'
  >;
}): {
  diffEditor: monacoEditor.editor.IStandaloneDiffEditor;
  diffNavigator: monacoEditor.editor.IDiffNavigator;
} => {
  useRegisterMonacoLanguageContribution(language);
  const [originalModel, setOriginalModel] =
    useState<monacoEditor.editor.ITextModel | null>();
  const [modifiedModel, setModifiedModel] =
    useState<monacoEditor.editor.ITextModel | null>(null);
  const diffEditor = useMemo<monacoEditor.editor.IStandaloneDiffEditor>(() => {
    const node = document.createElement('div');
    node.className = 'w-full h-full';
    node.dataset.chromatic = 'ignore'; // editor contents is shifting in chromatic. Disable monaco in chromatic.

    return monacoEditor.editor.createDiffEditor(node, {
      ...DIFF_EDITOR_OPTIONS,
    });
  }, []);

  const diffNavigator = useMemo(
    () =>
      monacoEditor.editor.createDiffNavigator(diffEditor, {
        alwaysRevealFirst: true,
      }),
    [diffEditor],
  );

  useEffect(() => {
    const newOriginalModel = monacoEditor.editor.createModel('', 'text/plain');
    setOriginalModel(newOriginalModel);

    const newModifiedModel = monacoEditor.editor.createModel('', 'text/plain');
    setModifiedModel(newModifiedModel);

    monacoEditor.editor.setModelLanguage(newOriginalModel, language);
    monacoEditor.editor.setModelLanguage(newModifiedModel, language);

    diffEditor.setModel({
      original: newOriginalModel,
      modified: newModifiedModel,
    });
    return () => {
      setOriginalModel(null);
      setModifiedModel(null);
      // Note this takes care of disposing the underlying editors/models.
      diffEditor.dispose();
      diffNavigator.dispose();
    };
  }, [diffEditor, diffNavigator, language]);

  useEffect(() => {
    if (originalFile) originalModel?.setValue(originalFile);
  }, [originalModel, originalFile]);

  useEffect(() => {
    if (modifiedFile) modifiedModel?.setValue(modifiedFile);
  }, [modifiedModel, modifiedFile]);

  useEffect(() => {
    // When the monaco options change, update the diff editor options
    diffEditor.updateOptions({
      ...DIFF_EDITOR_OPTIONS,
      ...monacoOptions,
    });
  }, [diffEditor, monacoOptions, originalModel, modifiedModel]);

  return { diffEditor, diffNavigator };
};

export const StudioDiffEditorContainer = ({
  className,
  editorHeight,
  diffEditor,
}: {
  className?: string;
  editorHeight?: string;
  diffEditor: monacoEditor.editor.IStandaloneDiffEditor;
}) => {
  const diffEditorContainerRef = useRef<HTMLDivElement>(null);
  const isChromatic = getIsChromatic();

  useEffect(() => {
    const domNode = diffEditor.getContainerDomNode();
    if (domNode) {
      diffEditorContainerRef.current?.appendChild(domNode);
      diffEditor.layout();
    }
  }, [diffEditor]);

  return (
    <div
      ref={isChromatic ? undefined : diffEditorContainerRef}
      className={className}
      css={css`
        .monaco-diff-editor .delete-sign {
          color: ${text.error} !important;
          margin-left: 4px;
        }
        .monaco-diff-editor .insert-sign {
          color: ${text.success} !important;
          margin-left: 4px;
        }
      `}
      // Note that Monaco dynamically reads the height of the container and
      // matches it.
      style={{ height: editorHeight }}
    >
      {isChromatic && (
        // Even with the monaco node ignored in chromatic, we still get flake
        // from the layout height changing. We've tried a number of fixes here
        // which didnt work. Don't render monaco at all in chromatic.
        <div className="mb-1 font-semibold text-warning">
          (Monaco hidden to avoid hard to fix chromatic flake.)
        </div>
      )}
    </div>
  );
};
